// An incident's links to other entities, as the groups the grouped relation
// list shows. Pure functions: the sections of the view are built from them.

import {
  IncidentLink,
  IncidentProduct,
  IncidentProducts,
} from "../../types/incidentDetail";
import { RelationGroup } from "../Detail/GroupedRelationList";
import { codeLabel } from "../Detail/format";

const productItem = (item: IncidentProduct, noteRelation: boolean) => ({
  entity: item.product,
  // A product linked by another relation says which
  note:
    [noteRelation ? codeLabel(item.relation) : "", item.summary]
      .filter(Boolean)
      .join(" · ") || null,
  derived: item.derived,
  sources: item.sources,
});

// The products by how they are in the incident, in the fixed order
export const productGroups = (products: IncidentProducts): RelationGroup[] => [
  {
    label: "Used in",
    items: products.used_in.map((item) => productItem(item, false)),
  },
  {
    label: "Targeted in",
    items: products.targeted_in.map((item) => productItem(item, false)),
  },
  {
    label: "Involved in",
    items: products.involved_in.map((item) => productItem(item, false)),
  },
  {
    label: "Other",
    items: products.other.map((item) => productItem(item, true)),
  },
];

// The relation that says no more than that the entity has the incident
const HAS_INCIDENT = "has_incident";

// How a group of links is named: no label for the generic relation, where
// the subhead of the kind says enough; the relation in words for another
const linkLabel = (relation: string): string =>
  relation === HAS_INCIDENT ? "" : codeLabel(relation);

// Organizations, facilities or projects, grouped by their relation
export const linkGroups = (links: IncidentLink[]): RelationGroup[] => {
  const groups = new Map<string, RelationGroup>();
  for (const link of links) {
    const label = linkLabel(link.relation);
    const group = groups.get(label) ?? { label, items: [] };
    group.items.push({
      entity: link.entity,
      note: link.summary,
      sources: link.sources,
    });
    groups.set(label, group);
  }
  return Array.from(groups.values());
};

// The number of entities in the groups
export const countRelations = (groups: RelationGroup[]): number =>
  groups.reduce((count, group) => count + group.items.length, 0);
